import { useEffect, useState } from 'react'
import { getLeads, updateLeadStatus, deleteLead, Lead } from '@/services/leads'
import { getSales, Sale } from '@/services/sales'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Loader2, Settings2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { LeadsTable } from '@/components/admin/LeadsTable'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DashboardCustomizerDialog } from '@/components/admin/DashboardCustomizerDialog'
import { QuickActions } from '@/components/admin/QuickActions'
import { CreateLeadDialog } from '@/components/admin/CreateLeadDialog'
import { CreateTaskDialog } from '@/components/admin/CreateTaskDialog'
import { MetricCards } from '@/components/admin/MetricCards'
import { RevenueChart } from '@/components/admin/RevenueChart'
import { subDays, startOfMonth, startOfYear } from 'date-fns'

const Dashboard = () => {
  const { toast } = useToast()

  // Data State
  const [leads, setLeads] = useState<Lead[]>([])
  const [sales, setSales] = useState<Sale[]>([])
  const [loading, setLoading] = useState(true)

  // UI State
  const [timeRange, setTimeRange] = useState('30d')
  const [customizerOpen, setCustomizerOpen] = useState(false)
  const [createLeadOpen, setCreateLeadOpen] = useState(false)
  const [createTaskOpen, setCreateTaskOpen] = useState(false)

  // Preferences State
  const [visibleMetrics, setVisibleMetrics] = useState({
    total_sales: true,
    active_users: true,
    new_leads: true,
    growth: true,
  })

  // Calculate Date Range
  const getStartDate = () => {
    const now = new Date()
    switch (timeRange) {
      case '7d':
        return subDays(now, 7)
      case '30d':
        return subDays(now, 30)
      case 'month':
        return startOfMonth(now)
      case 'year':
        return startOfYear(now)
      case 'all':
        return undefined
      default:
        return subDays(now, 30)
    }
  }

  const fetchData = async () => {
    setLoading(true)
    const startDate = getStartDate()

    try {
      const [leadsRes, salesRes] = await Promise.all([
        getLeads(startDate),
        getSales(startDate),
      ])

      if (leadsRes.error) throw leadsRes.error
      if (salesRes.error) throw salesRes.error

      setLeads(leadsRes.data || [])
      setSales(salesRes.data || [])
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar dados',
        description: 'Não foi possível atualizar o dashboard.',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [timeRange])

  // Handlers
  const handleUpdateStatus = async (id: string, status: string) => {
    const previousLeads = [...leads]
    setLeads(leads.map((l) => (l.id === id ? { ...l, status } : l)))

    try {
      const { error } = await updateLeadStatus(id, status)
      if (error) throw error
      toast({ title: 'Status atualizado' })
    } catch {
      setLeads(previousLeads)
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar',
        description: 'Não foi possível atualizar o status.',
      })
    }
  }

  const handleDeleteLead = async (id: string) => {
    const previousLeads = [...leads]
    setLeads(leads.filter((l) => l.id !== id))

    try {
      const { error } = await deleteLead(id)
      if (error) throw error
      toast({ title: 'Lead eliminado' })
    } catch {
      setLeads(previousLeads)
      toast({
        variant: 'destructive',
        title: 'Erro ao eliminar',
        description: 'Não foi possível eliminar o lead.',
      })
    }
  }

  const toggleMetric = (key: string) => {
    setVisibleMetrics((prev) => ({
      ...prev,
      [key]: !prev[key as keyof typeof prev],
    }))
  }

  // Calculated Metrics
  const calculatedMetrics = {
    total_sales: sales.reduce((acc, curr) => acc + curr.amount, 0),
    active_users: leads.filter((l) => l.status !== 'closed').length,
    new_leads: leads.filter((l) => l.status === 'new').length,
    growth: 12, // Mocked growth for now as we don't have historical comparison logic implemented yet
  }

  if (loading && leads.length === 0 && sales.length === 0) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">
            Visão geral da performance do negócio.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Selecione o período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Últimos 7 dias</SelectItem>
              <SelectItem value="30d">Últimos 30 dias</SelectItem>
              <SelectItem value="month">Este mês</SelectItem>
              <SelectItem value="year">Este ano</SelectItem>
              <SelectItem value="all">Todo o tempo</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={() => setCustomizerOpen(true)}>
            <Settings2 className="mr-2 h-4 w-4" />
            Personalizar Dashboard
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <QuickActions
        onAddLead={() => setCreateLeadOpen(true)}
        onCreateTask={() => setCreateTaskOpen(true)}
      />

      {/* Metrics Grid */}
      <MetricCards metrics={calculatedMetrics} visible={visibleMetrics} />

      {/* Charts Section */}
      <div className="grid gap-6 md:grid-cols-1">
        <RevenueChart sales={sales} />
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle>Gestão de Leads</CardTitle>
          <CardDescription>
            Visualize, filtre e gerencie todos os pedidos de contacto recentes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <LeadsTable
            leads={leads}
            onUpdateStatus={handleUpdateStatus}
            onDelete={handleDeleteLead}
          />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <DashboardCustomizerDialog
        open={customizerOpen}
        onOpenChange={setCustomizerOpen}
        visibleMetrics={visibleMetrics}
        onToggleMetric={toggleMetric}
      />

      <CreateLeadDialog
        open={createLeadOpen}
        onOpenChange={setCreateLeadOpen}
        onSuccess={fetchData}
      />

      <CreateTaskDialog
        open={createTaskOpen}
        onOpenChange={setCreateTaskOpen}
      />
    </div>
  )
}

export default Dashboard
